<template>
    <Modal id="order-preview-modal"
           data-component="OrderPreviewModal"
           :title="title">
        <PageLoadingSpinner v-if="loading"></PageLoadingSpinner>

        <CartOverview :stashed-cart="stashedCart">
            <template v-slot:statuses>
                <CartOverviewStatuses v-if="stashedCart.currentCart.originalOrder"
                                      :cart="stashedCart.currentCart" />
            </template>
        </CartOverview>

        <template v-if="stashedCart.currentCart.restoredKitchenOrder">
            <KitchenOrderDetailsTable :current-items="stashedCart.currentCart.restoredKitchenOrder.items" />
        </template>
        <template v-else>
            <template v-for="cartItemGroup in cartItemGroups"
                      :key="cartItemGroup.key">
                <OrderPreviewLineGroup v-if="cartItemGroup.session"
                                       :cart-item-group="cartItemGroup" />
                <OrderPreviewLines v-if="!cartItemGroup.session"
                                   :lines="cartItemGroup.items" />
            </template>
        </template>

        <div v-if="stashedCart.currentCart.notes"
             class="order-preview-notes mt-1">
            {{ stashedCart.currentCart.notes }}
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('CANCEL') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           data-analytics-id="Order Preview / Print Bill"
                           class="print-btn"
                           :loading="printingBill"
                           @click="printBill">
                    {{ l('KITCHEN_MODAL.PRINT_BILL_BUTTON') }}
                </SquareBtn>

                <!-- Delete btn -->
                <RoundBtn v-if="showDelete"
                          data-analytics-id="Order Preview / Delete"
                          color="danger"
                          icon="fa-solid fa-trash-can"
                          filled
                          :loading="stashedCart.isDeleting"
                          @click="parentModal.deleteCartWithConfirmation(stashedCart, modalID)"></RoundBtn>

                <!-- Restore btn -->
                <RoundBtn v-if="showRestore"
                          data-analytics-id="Order Preview / Restore"
                          color="success"
                          icon="fa-solid fa-share"
                          filled
                          :loading="stashedCart.isRestoring"
                          @click="parentModal.restoreCart(stashedCart, modalID)"></RoundBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import CartOverviewStatuses from '@/epos/vue/modals/common/CartOverviewStatuses';
import KitchenOrderDetailsTable from '@/vue/components/KitchenOrderDetails/KitchenOrderDetailsTable';
import Modal from '@/vue/components/Modals/Modal';
import OrderPreviewLineGroup from '@/epos/vue/modals/OrderPreview//OrderPreviewLineGroup';
import OrderPreviewLines from '@/epos/vue/modals/OrderPreview/OrderPreviewLines';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import RoundBtn from '@/vue/components/RoundBtn';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { createCartItemGroupsFromItems } from '@/libraries/DigiTickets/Cart/cartItemGroups';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'OrderPreviewModal',

    components: {
        KitchenOrderDetailsTable,
        Modal,
        SquareBtn,
        RoundBtn,
        PageLoadingSpinner,
        CartOverview,
        CartOverviewStatuses,
        OrderPreviewLineGroup,
        OrderPreviewLines,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {StashedCart}
         */
        stashedCart: {
            type: Object,
            required: true,
        },
        title: {
            type: String,
            default: 'ORDER_PREVIEW_MODAL.ORDER_TITLE',
        },
        icon: {
            type: String,
            default: 'fa-rectangle-list',
        },
        showDelete: {
            type: Boolean,
            default: true,
        },
        showRestore: {
            type: Boolean,
            default: true,
        },
        parentModal: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            loading: false,
            printingBill: false,
            cartItemGroups: null,
        };
    },

    created() {
        this.cartItemGroups = createCartItemGroupsFromItems(this.stashedCart.currentCart.itemList);
    },

    methods: {
        printBill() {
            this.printingBill = true;
            container.cartStashHelperService
                .stashToOrder(this.stashedCart)
                .then((order) => {
                    return container.receiptPrinter.printBill(order);
                })
                .finally(() => {
                    this.printingBill = false;
                });
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";
@import (less, reference) "../../../../less/mixins.less";

.order-preview-notes {
    font-weight: bold;
    color: @brand-danger;
}

#order-preview-modal {
    .modl-footer {
        .round-btn {
            width: 48px;
            height: 48px;
            float: right;
            margin: 0 0 0 10px;

            i {
                font-size: 18px;
            }
        }

        .print-btn {
            margin-right: 0;
        }
    }
}

@media (max-width: @mobile-max) {
    #order-preview-modal {
        .modl-footer {
            flex-direction: row;
            flex-wrap: wrap;
            .round-btn {
                margin: 0;
                border-radius: @border-radius-base;
                width:calc(50% - @half-gutter-width-mobile);
                // copied from btn-lg
                padding: 9px 12px;
                line-height: 27px;
                border-radius: 6px;
                min-height: 48px;
                height: auto;
                font-size: 16px;
            }
            .btn {
                &:nth-child(1) {
                    order: 3;
                }

                &:nth-child(2) {
                    order: 2;
                }

                &:nth-child(3) {
                    order: 1;
                }

                &:nth-child(4) {
                    order: 2;
                }
            }
        }
    }
}
</style>
